import { Button } from '@/components/ui/button'
import { useGenerateAudio } from '@/hooks/use-learning'
import type { AudioGenerationStatus, LearningModule } from '@/types'
import { AlertCircle, Headphones, Loader2, Pause, Play, RotateCcw, Volume2 } from 'lucide-react'
import type { MouseEvent } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

const PLAYBACK_SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface AudioPlayerProps {
  module: LearningModule
  topicId: number
}

export function AudioPlayer({ module, topicId }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [playbackSpeed, setPlaybackSpeed] = useState(1)
  const [isExpanded, setIsExpanded] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)
  const progressRef = useRef<HTMLDivElement>(null)
  const generateAudio = useGenerateAudio(topicId)

  const status = module.audio_generation_status as AudioGenerationStatus
  const audioUrl = module.audio_narrator_url

  // SSE events automatically refetch module when audio generation status changes

  // Reset playback state when URL changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: audioUrl intentionally triggers reset
  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
    setDuration(0)
  }, [audioUrl])

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play()
    }
    setIsPlaying(!isPlaying)
  }, [isPlaying])

  const handleTimeUpdate = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    setCurrentTime(audio.currentTime)
  }, [])

  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current
    if (!audio) return
    setDuration(audio.duration)
  }, [])

  const handleEnded = useCallback(() => {
    setIsPlaying(false)
  }, [])

  const handleSpeedChange = useCallback(() => {
    const currentIdx = PLAYBACK_SPEEDS.indexOf(playbackSpeed as (typeof PLAYBACK_SPEEDS)[number])
    const nextIdx = (currentIdx + 1) % PLAYBACK_SPEEDS.length
    const newSpeed = PLAYBACK_SPEEDS[nextIdx] ?? 1
    setPlaybackSpeed(newSpeed)
    if (audioRef.current) {
      audioRef.current.playbackRate = newSpeed
    }
  }, [playbackSpeed])

  const handleProgressClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const bar = progressRef.current
      const audio = audioRef.current
      if (!bar || !audio || !duration) return

      const rect = bar.getBoundingClientRect()
      const percent = (e.clientX - rect.left) / rect.width
      audio.currentTime = percent * duration
    },
    [duration],
  )

  const handleGenerate = useCallback(() => {
    generateAudio.mutate()
  }, [generateAudio])

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0

  // No audio and not generating - show generate button
  if (!status) {
    return (
      <div className="border rounded-lg p-4 bg-muted/30">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex items-center justify-between text-sm"
        >
          <span className="flex items-center gap-2 text-muted-foreground">
            <Volume2 className="h-4 w-4" />
            Audio Narration
          </span>
          <span className="text-xs text-muted-foreground">{isExpanded ? 'Hide' : 'Show'}</span>
        </button>
        {isExpanded && (
          <div className="mt-3 text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Generate audio narration for this module.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={handleGenerate}
              disabled={generateAudio.isPending}
            >
              {generateAudio.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : (
                <Headphones className="h-4 w-4 mr-1" />
              )}
              Generate Audio
            </Button>
          </div>
        )}
      </div>
    )
  }

  // Pending or generating - show spinner
  if (status === 'pending' || status === 'generating') {
    return (
      <div className="border rounded-lg p-4 bg-muted/30">
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {status === 'pending' ? 'Audio generation queued...' : 'Generating audio...'}
          </span>
        </div>
      </div>
    )
  }

  // Failed - show error with retry
  if (status === 'failed') {
    return (
      <div className="border rounded-lg p-4 bg-destructive/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm text-muted-foreground">Audio generation failed</span>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleGenerate}
            disabled={generateAudio.isPending}
          >
            <RotateCcw className="h-3 w-3 mr-1" />
            Retry
          </Button>
        </div>
        {module.audio_generation_error && (
          <p className="text-xs text-muted-foreground mt-2">{module.audio_generation_error}</p>
        )}
      </div>
    )
  }

  // Completed - show narrator player
  return (
    <div className="border rounded-lg p-4 bg-muted/30">
      <div className="flex items-center gap-1.5 mb-3 text-xs font-medium text-muted-foreground">
        <Volume2 className="h-3 w-3" />
        Narrator
      </div>

      {audioUrl ? (
        <>
          {/* Hidden audio element */}
          {/* biome-ignore lint/a11y/useMediaCaption: generated audio has no captions */}
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
            onEnded={handleEnded}
            preload="metadata"
          />

          {/* Controls */}
          <div className="flex items-center gap-3">
            {/* Play/Pause */}
            <button
              type="button"
              onClick={handlePlayPause}
              className="shrink-0 h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-colors"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
            </button>

            {/* Progress bar */}
            <div className="flex-1 min-w-0">
              <div
                ref={progressRef}
                className="h-2 bg-muted rounded-full cursor-pointer relative"
                onClick={handleProgressClick}
                onKeyDown={undefined}
                role="slider"
                aria-valuemin={0}
                aria-valuemax={duration}
                aria-valuenow={currentTime}
                tabIndex={0}
              >
                <div
                  className="absolute inset-y-0 left-0 bg-primary rounded-full transition-[width] duration-150"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <div className="flex justify-between mt-1 text-[10px] text-muted-foreground tabular-nums">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Speed control */}
            <button
              type="button"
              onClick={handleSpeedChange}
              className="shrink-0 text-xs font-medium text-muted-foreground hover:text-foreground px-1.5 py-0.5 rounded bg-muted transition-colors tabular-nums"
            >
              {playbackSpeed}x
            </button>
          </div>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">Narrator audio not available.</p>
      )}
    </div>
  )
}
